import { useCallback, useEffect, useRef, useState } from "react";
import { ASSET_FILES } from "../slides";
import { publicAsset } from "../publicAsset";

/** One URL per image — avoids thumb+probe races and doubled requests that stall in dev (tiles blank until hover). */
function useGalleryAssetSrc(file) {
  const src = publicAsset(file);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const onError = useCallback(() => {
    setFailed(true);
  }, []);

  return { src, failed, onError };
}

function GalleryPhoto({ assetKey, index, onOpen }) {
  const file = ASSET_FILES[assetKey];
  const { src, onError, failed } = useGalleryAssetSrc(file);

  if (failed) {
    return (
      <figure
        className="slide-gallery__cell slide-gallery__cell--placeholder"
        aria-label={`Missing image: ${file}`}
      >
        <div className="slide-gallery__ph">
          <span className="slide-gallery__ph-title">Missing file</span>
          <code className="slide-gallery__ph-file">{file}</code>
        </div>
      </figure>
    );
  }

  return (
    <figure className="slide-gallery__cell">
      <button
        type="button"
        className="slide-gallery__thumb"
        onClick={() => onOpen(index)}
        aria-label={`Photo ${index + 1}`}
      >
        <img
          key={src}
          src={src}
          alt=""
          className="slide-gallery__img"
          loading="eager"
          decoding="sync"
          onError={onError}
        />
      </button>
    </figure>
  );
}

function LightboxImg({ file }) {
  const { src, onError, failed } = useGalleryAssetSrc(file);
  if (failed) {
    return (
      <p className="lightbox__err" role="alert">
        Could not load image.
      </p>
    );
  }
  return (
    <img
      key={src}
      src={src}
      alt=""
      className="lightbox__img"
      decoding="sync"
      fetchPriority="high"
      onError={onError}
    />
  );
}

export function SlideMedia({ images }) {
  const [openIndex, setOpenIndex] = useState(null);
  const closeRef = useRef(null);
  const openedFromClosed = useRef(false);
  const sig = images?.join("|") ?? "";

  useEffect(() => {
    setOpenIndex(null);
  }, [sig]);

  useEffect(() => {
    if (openIndex === null) {
      openedFromClosed.current = false;
      return;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (!openedFromClosed.current) {
      openedFromClosed.current = true;
      queueMicrotask(() => closeRef.current?.focus());
    }

    const onKey = (e) => {
      if (!images?.length) return;
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setOpenIndex(null);
      } else if (e.key === "ArrowRight" && images.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        setOpenIndex((i) => (i === null ? null : (i + 1) % images.length));
      } else if (e.key === "ArrowLeft" && images.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        setOpenIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
      } else if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey, true);
    };
  }, [openIndex, images]);

  const close = useCallback(() => setOpenIndex(null), []);

  if (!images?.length) return null;

  const openKey = openIndex !== null ? images[openIndex] : null;
  const openFile = openKey ? ASSET_FILES[openKey] : "";

  return (
    <>
      <div className="slide-gallery" data-count={images.length}>
        {images.map((key, i) => (
          <GalleryPhoto key={`${String(key)}-${i}`} assetKey={key} index={i} onOpen={setOpenIndex} />
        ))}
      </div>

      {openIndex !== null && openKey && openFile ? (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged photo"
          onClick={close}
        >
          <button
            ref={closeRef}
            type="button"
            className="lightbox__close"
            onClick={close}
            aria-label="Close enlarged view"
          >
            ×
          </button>
          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((i) => (i === null ? null : (i - 1 + images.length) % images.length));
                }}
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                type="button"
                className="lightbox__nav lightbox__nav--next"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenIndex((i) => (i === null ? null : (i + 1) % images.length));
                }}
                aria-label="Next photo"
              >
                ›
              </button>
            </>
          ) : null}
          <div className="lightbox__frame" onClick={(e) => e.stopPropagation()}>
            <LightboxImg file={openFile} />
          </div>
        </div>
      ) : null}
    </>
  );
}
